import { useState, useRef, useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';

import FormTextInput from '#globalComponents/Forms/TextInput';
import { GERMAN_STATE_LABELS } from '#lib/constants/germanStates';

import { fetchCitySuggestions } from '../../../api/cityAutocompleteApi';

const DEBOUNCE_MS = 300;

/**
 * Location input with city suggestions from the real company index.
 * Picking a suggestion inserts the exact stored city value (e.g.
 * "Frankfurt a. Main"), which the HR search matches exactly; the state label
 * disambiguates same-named cities (Frankfurt a. Main vs. Frankfurt (Oder)).
 * Free text remains allowed as a fallback (e.g. for Maps searches abroad).
 */
export const CityAutocompleteInput = ({ id, label, placeholder, value, onChange }) => {
	const [suggestions, setSuggestions] = useState([]);
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef(null);
	const debounceRef = useRef(null);

	const performSearch = useCallback(async (query) => {
		const q = query.trim();
		if (q.length < 2) {
			setSuggestions([]);
			setIsOpen(false);
			return;
		}
		try {
			const data = await fetchCitySuggestions(q);
			setSuggestions(data);
			setIsOpen(data.length > 0);
		} catch {
			// Suggestions are a convenience — free-text input keeps working
			setSuggestions([]);
			setIsOpen(false);
		}
	}, []);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, []);

	const handleChange = (e) => {
		const next = e.target.value;
		onChange(next);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => performSearch(next), DEBOUNCE_MS);
	};

	const handleSelect = (suggestion) => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		onChange(suggestion.city);
		setSuggestions([]);
		setIsOpen(false);
	};

	return (
		<div ref={wrapperRef} className="relative">
			{label && (
				<label htmlFor={id} className="block text-xs text-bgBase-800 mb-1">
					{label}
				</label>
			)}
			<FormTextInput
				id={id}
				placeholder={placeholder}
				value={value || ''}
				onChange={handleChange}
				onFocus={() => suggestions.length > 0 && setIsOpen(true)}
				onKeyDown={(e) => {
					if (e.key !== 'Escape' || !isOpen) return;
					// Closes the suggestions only; the surrounding modal must not see the key.
					e.stopPropagation();
					setIsOpen(false);
				}}
				autoComplete="off"
			/>
			{isOpen && suggestions.length > 0 && (
				<ul className="absolute z-40 w-full mt-1 bg-white border border-bgBase-500 rounded-card shadow-elevated max-h-52 overflow-y-auto py-1">
					{suggestions.map((s) => (
						<li key={`${s.city}|${s.state}`}>
							<button
								type="button"
								onClick={() => handleSelect(s)}
								className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs text-left text-gray-700 hover:bg-gray-50"
							>
								<span className="truncate font-medium">{s.city}</span>
								<span className="flex items-center gap-1.5 flex-shrink-0 text-[10px] text-gray-400">
									{s.state && GERMAN_STATE_LABELS[s.state] && (
										<span className="px-1 py-0.5 bg-gray-100 rounded">
											{GERMAN_STATE_LABELS[s.state]}
										</span>
									)}
									{typeof s.count === 'number' && (
										<span>{s.count.toLocaleString('de-DE')} Firmen</span>
									)}
								</span>
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

CityAutocompleteInput.propTypes = {
	id: PropTypes.string,
	label: PropTypes.string,
	placeholder: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
};
